import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { concat } from '@ember/helper';
import type { ComponentLike } from '@glint/template';
import FormField from './form-field';

// ---------------------------------------------------------------------------
// Input – reimagined text input with Tailwind styling
// ---------------------------------------------------------------------------

/**
 * Supported HTML input types for the Input component.
 */
export type InputType = 'text' | 'email' | 'number' | 'password' | 'tel' | 'date' | 'url';

/**
 * Size variants consistent with all reimagined form components.
 */
export type InputSize = 'sm' | 'md' | 'lg';

interface InputSignature {
  Args: {
    // --- Value ---
    /** Current input value */
    value?: string | number;
    /** Called on every keystroke with the new value */
    onChange?: (value: string) => void;
    /** Called when the input loses focus */
    onBlur?: () => void;

    // --- Presentation ---
    /** Label displayed above the input */
    label?: string;
    /** Hint text displayed below the input */
    hint?: string;
    /** Placeholder text */
    placeholder?: string;
    /** HTML input type @default "text" */
    type?: InputType;
    /** Size variant @default "md" */
    size?: InputSize;

    // --- State ---
    /** Validation error messages */
    errors?: string[];
    /** Show required indicator on label */
    isRequired?: boolean;
    /** Disable the input */
    isDisabled?: boolean;
    /** Make the input readonly */
    isReadonly?: boolean;

    // --- Extras ---
    /** Icon component rendered inside the input on the left */
    leadingIcon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Icon component rendered inside the input on the right */
    trailingIcon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** HTML inputmode attribute */
    inputmode?: string;
    /** HTML autocomplete attribute */
    autocomplete?: string;
    /** Max character length */
    maxlength?: number;
    /** HTML name attribute */
    name?: string;
  };
  Element: HTMLInputElement;
}

// ---------------------------------------------------------------------------
// Size classes — matches reimagined Select height/font scale
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Record<InputSize, { input: string; icon: number }> = {
  sm: { input: 'h-[32px] py-1 px-2.5 text-sm', icon: 14 },
  md: { input: 'h-[40px] py-2 px-3 text-sm', icon: 16 },
  lg: { input: 'h-[48px] py-2.5 px-3.5 text-base', icon: 18 },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined Input — a zero-dependency text input component.
 *
 * Controlled input that fires `@onChange` on every keystroke.
 * Works standalone with `@value`/`@onChange`/`@errors`, or as a field inside the
 * `<Form>` orchestrator (`tio-reimagined/components/form`).
 *
 * Styling matches the reimagined Select: violet focus rings, sm/md/lg sizes,
 * consistent dark mode treatment, and `...attributes` for escape-hatch
 * customisation (e.g. modifiers like `{{inputmask}}`).
 *
 * @example Basic usage
 * ```gts
 * import Input from 'tio-reimagined/components/input';
 *
 * <Input
 *   @label="Email"
 *   @type="email"
 *   @value={{this.email}}
 *   @onChange={{this.setEmail}}
 *   @placeholder="you@example.com"
 *   @isRequired={{true}}
 * />
 * ```
 *
 * @example With errors
 * ```gts
 * <Input
 *   @label="Name"
 *   @value={{this.name}}
 *   @onChange={{this.setName}}
 *   @errors={{array "Name is required"}}
 * />
 * ```
 *
 * @example With leading icon
 * ```gts
 * import { PhMagnifyingGlass } from 'ember-phosphor-icons';
 *
 * <Input
 *   @label="Search"
 *   @value={{this.query}}
 *   @onChange={{this.setQuery}}
 *   @leadingIcon={{PhMagnifyingGlass}}
 *   @placeholder="Search employees…"
 * />
 * ```
 */
export default class Input extends Component<InputSignature> {
  private _fieldId = `reimagined-input-${guidFor(this)}`;

  // ---- Derived state --------------------------------------------------------

  private get size(): InputSize {
    return this.args.size ?? 'md';
  }

  private get sizeClasses() {
    return SIZE_CLASSES[this.size];
  }

  private get type(): InputType {
    return this.args.type ?? 'text';
  }

  private get hasErrors(): boolean {
    return Boolean(this.args.errors?.length);
  }

  private get hasLeadingIcon(): boolean {
    return Boolean(this.args.leadingIcon);
  }

  private get hasTrailingIcon(): boolean {
    return Boolean(this.args.trailingIcon);
  }

  private get inputClasses(): string {
    const base = [
      'w-full rounded-lg border bg-white dark:bg-gray-900',
      'text-gray-900 dark:text-white',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      'transition-colors duration-150',
      'focus:outline-none',
      this.sizeClasses?.input,
    ];

    // Leading icon padding
    if (this.hasLeadingIcon) {
      base.push(this.size === 'sm' ? 'pl-8' : this.size === 'lg' ? 'pl-11' : 'pl-10');
    }

    // Trailing icon padding
    if (this.hasTrailingIcon) {
      base.push(this.size === 'sm' ? 'pr-8' : this.size === 'lg' ? 'pr-11' : 'pr-10');
    }

    // State styles
    if (this.args.isDisabled) {
      base.push('opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700');
    } else if (this.hasErrors) {
      base.push(
        'border-red-500 dark:border-red-400',
        'focus:ring-2 focus:ring-red-500/20 focus:border-red-500'
      );
    } else {
      base.push(
        'border-gray-300 dark:border-gray-600',
        'hover:border-gray-400 dark:hover:border-gray-500',
        'focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20'
      );
    }

    return base.join(' ');
  }

  // ---- Event handlers -------------------------------------------------------

  handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.args.onChange?.(input.value);
  };

  handleBlur = () => {
    this.args.onBlur?.();
  };

  // ---- Template -------------------------------------------------------------

  <template>
    <FormField
      @label={{@label}}
      @hint={{@hint}}
      @errors={{@errors}}
      @isRequired={{@isRequired}}
      @isDisabled={{@isDisabled}}
      @size={{this.size}}
      @fieldId={{this._fieldId}}
      as |field|
    >
      <div class="relative">
        {{! Leading icon }}
        {{#if this.hasLeadingIcon}}
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none"
            aria-hidden="true"
          >
            {{#let @leadingIcon as |Icon|}}
              {{#if Icon}}
                <Icon @size={{this.sizeClasses.icon}} />
              {{/if}}
            {{/let}}
          </span>
        {{/if}}

        <input
          data-test-input
          id={{field.fieldId}}
          type={{this.type}}
          value={{@value}}
          name={{@name}}
          placeholder={{@placeholder}}
          disabled={{@isDisabled}}
          readonly={{@isReadonly}}
          required={{@isRequired}}
          inputmode={{@inputmode}}
          autocomplete={{@autocomplete}}
          maxlength={{@maxlength}}
          aria-invalid={{if field.hasErrors "true"}}
          aria-describedby={{if @hint (concat field.fieldId "-hint")}}
          class={{this.inputClasses}}
          {{on "input" this.handleInput}}
          {{on "blur" this.handleBlur}}
          ...attributes
        />

        {{! Trailing icon }}
        {{#if this.hasTrailingIcon}}
          <span
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none"
            aria-hidden="true"
          >
            {{#let @trailingIcon as |Icon|}}
              {{#if Icon}}
                <Icon @size={{this.sizeClasses.icon}} />
              {{/if}}
            {{/let}}
          </span>
        {{/if}}
      </div>
    </FormField>
  </template>
}

export type { InputSignature };
